/**
 * Navigation Bar Component
 * Handles responsive navigation with mobile hamburger menu
 *
 * Contract:
 * - This script may be bundled globally.
 * - It must silently no-op if the navbar is not present.
 */

// Bootstrap the contact system on every page so the nav Contact button works globally.
import '../contact/index.js';

class NavBar {
    constructor(root) {
        this.root = root;
        this.hamburger = root.querySelector('#hamburger-menu');
        this.menu = root.querySelector('#navbar-menu');
        this.dropdownItems = root.querySelectorAll('.navbar__item--dropdown');
        this.isOpen = false;

        this.init();
    }

    init() {
        // Hamburger menu click handler
        if (this.hamburger) {
            this.hamburger.addEventListener('click', () => this.toggleMenu());
        }

        // Dropdown menu handlers
        this.dropdownItems.forEach((item) => {
            const button = item.querySelector('.navbar__link--dropdown');
            if (button) {
                button.addEventListener('click', (e) => this.toggleDropdown(e, item));
            }

            // Allow submenu links to work on mobile
            const sublinks = item.querySelectorAll('.navbar__sublink');
            sublinks.forEach((link) => {
                link.addEventListener('click', () => {
                    if (window.innerWidth <= 1100) {
                        this.closeMenu();
                        this.closeAllDropdowns();
                    }
                });
            });
        });

        // Close menu when clicking outside navbar
        document.addEventListener('click', (e) => this.handleOutsideClick(e));

        // Close menu on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeMenu();
                this.closeAllDropdowns();
            }
        });

        // Close menu on window resize (when returning to desktop)
        window.addEventListener('resize', () => {
            if (window.innerWidth > 1100) {
                this.closeMenu();
                this.closeAllDropdowns();
            }
        });
    }

    toggleMenu() {
        if (this.isOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    openMenu() {
        this.isOpen = true;

        if (this.hamburger) {
            this.hamburger.classList.add('is-open');
            this.hamburger.setAttribute('aria-expanded', 'true');
        }

        if (this.menu) {
            this.menu.classList.add('is-open');
        }
    }

    closeMenu() {
        this.isOpen = false;

        if (this.hamburger) {
            this.hamburger.classList.remove('is-open');
            this.hamburger.setAttribute('aria-expanded', 'false');
        }

        if (this.menu) {
            this.menu.classList.remove('is-open');
        }
    }

    toggleDropdown(e, item) {
        // Only handle as toggle on mobile
        if (window.innerWidth > 1100) return;

        e.preventDefault();
        const isOpen = item.classList.contains('is-open');

        // Close all other dropdowns
        this.dropdownItems.forEach((otherItem) => {
            if (otherItem !== item) {
                otherItem.classList.remove('is-open');
                const otherButton = otherItem.querySelector('.navbar__link--dropdown');
                if (otherButton) {
                    otherButton.setAttribute('aria-expanded', 'false');
                }
            }
        });

        // Toggle current dropdown
        if (isOpen) {
            item.classList.remove('is-open');
            if (e.currentTarget) e.currentTarget.setAttribute('aria-expanded', 'false');
        } else {
            item.classList.add('is-open');
            if (e.currentTarget) e.currentTarget.setAttribute('aria-expanded', 'true');
        }
    }

    closeAllDropdowns() {
        this.dropdownItems.forEach((item) => {
            item.classList.remove('is-open');
            const button = item.querySelector('.navbar__link--dropdown');
            if (button) {
                button.setAttribute('aria-expanded', 'false');
            }
        });
    }

    handleOutsideClick(e) {
        if (window.innerWidth > 1100) return;
        if (!this.isOpen) return;
        if (!this.root) return;

        const target = e.target;
        if (target && !this.root.contains(target)) {
            this.closeMenu();
        }
    }
}

// Idempotent boot (prevents duplicate listeners if script is loaded twice)
function boot() {
    if (window.__navbarInitialized) return;

    const navbarRoot = document.querySelector('.navbar');
    if (!navbarRoot) return;

    window.__navbarInitialized = true;
    new NavBar(navbarRoot);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}

export default NavBar;
